// Need to use the React-specific entry point to import createApi
import config from "../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BookingStatus,
  Cart,
  CartStatus,
  EventFilter,
} from "../../../api/src/types";
import type {
  CartGetResponse,
  CartGetByIdResponse,
} from "../../../api/src/users/carts/route";
import type {
  CartItemPatchResponse,
  CartItemDeleteResponse,
  CartItemPostResponse,
} from "../../../api/src/users/carts/cart-items/route";
import { prepareHeaders } from "./utils";
import type { CheckoutPostResponse } from "../../../api/src/users/checkout/route";
import type { PromoCodePostResponse } from "../../../api/src/users/carts/promo-codes/route";
import type {
  PurchaseItemPatchResponse,
  PurchaseItemsGetResponse,
  PurchaseItemWithEventsGetResponse,
} from "../../../api/src/users/purchase-items/route";
import type { ProductsGetResponse } from "../../../api/src/products/route";
import { PurchaseItemRefundResponse } from "../../../api/src/admin/purchase-items/route";
import { documentsApi } from "./documents";
import { usersApi } from "./users";

export const paymentsApi = createApi({
  reducerPath: "paymentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["purchaseItems", "carts", "products"],
  endpoints: (builder) => ({
    getProducts: builder.query<ProductsGetResponse, void>({
      query: () => ({
        url: "products",
        method: "GET",
      }),
      providesTags: ["products"],
    }),
    getAttorneyServicesPurchasedByUser: builder.query<
      PurchaseItemsGetResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `users/${id}/purchase-items?category=ATTORNEY_SERVICE`,
        method: "GET",
      }),
      providesTags: ["purchaseItems"],
    }),
    getPurchaseItemsByUser: builder.query<
      PurchaseItemsGetResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `users/${id}/purchase-items`,
        method: "GET",
      }),
      providesTags: ["purchaseItems"],
    }),
    getCartsByUser: builder.query<
      CartGetResponse,
      { id: string; transactionStatus: string }
    >({
      query: ({ id, transactionStatus }) => ({
        url: `users/${id}/carts?transactionStatus=${transactionStatus}`,
        method: "GET",
      }),
      providesTags: ["carts"],
    }),
    getCartById: builder.query<
      CartGetByIdResponse,
      { id: string; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `users/${userId}/carts/${id}`,
        method: "GET",
      }),
      providesTags: ["carts"],
    }),
    createCart: builder.mutation<Cart, { id: string; cartStatus: CartStatus }>({
      query: ({ id, cartStatus }) => ({
        url: `users/${id}/carts`,
        method: "POST",
        body: {
          cartStatus,
        },
      }),
      invalidatesTags: ["carts"],
    }),

    addCartItem: builder.mutation<
      CartItemPostResponse,
      {
        userId: string;
        cartId: string;
        productId: string;
        quantity: number;
        chosenAttorneyId?: string;
      }
    >({
      query: ({ userId, cartId, productId, quantity, chosenAttorneyId }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items`,
        method: "POST",
        body: {
          quantity,
          productId,
          ...(chosenAttorneyId && { chosenAttorneyId }),
        },
      }),
      invalidatesTags: ["carts"],
    }),
    updateCartItem: builder.mutation<
      CartItemPatchResponse,
      {
        userId: string;
        cartId: string;
        cartItemId: string;
        quantity: number;
        deletedAt?: Date;
        chosenAttorneyId?: string;
      }
    >({
      query: ({
        userId,
        cartId,
        cartItemId,
        quantity,
        deletedAt,
        chosenAttorneyId,
      }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items/${cartItemId}`,
        method: "PATCH",
        body: { quantity, deletedAt, chosenAttorneyId },
      }),
      invalidatesTags: ["carts"],
    }),
    removeCartItem: builder.mutation<
      CartItemDeleteResponse,
      { userId: string; cartId: string; cartItemId: string }
    >({
      query: ({ userId, cartId, cartItemId }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items/${cartItemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["carts"],
    }),
    createCheckout: builder.mutation<
      CheckoutPostResponse,
      { id: string; cartId: string; paymentMethod: "card" | "installments" }
    >({
      query: ({ id, cartId, paymentMethod }) => ({
        url: `users/${id}/checkout`,
        method: "POST",
        body: {
          cartId,
          paymentMethod,
        },
      }),
    }),
    applyPromoCode: builder.mutation<
      PromoCodePostResponse,
      { userId: string; cartId: string; promoCode: string }
    >({
      query: ({ userId, cartId, promoCode }) => ({
        url: `users/${userId}/carts/${cartId}/promo-codes`,
        method: "POST",
        body: {
          promoCode,
        },
      }),
      invalidatesTags: ["carts"],
    }),
    removePromoCode: builder.mutation<
      PromoCodePostResponse,
      { userId: string; cartId: string }
    >({
      query: ({ userId, cartId }) => ({
        url: `users/${userId}/carts/${cartId}/promo-codes`,
        method: "DELETE",
      }),
      invalidatesTags: ["carts"],
    }),
    updatePurchaseItem: builder.mutation<
      PurchaseItemPatchResponse,
      {
        userId: string;
        purchaseItemId: string;
        bookingStatus: BookingStatus;
        attorneyId: string;
        documentId: string;
      }
    >({
      query: ({
        userId,
        purchaseItemId,
        bookingStatus,
        attorneyId,
        documentId,
      }) => ({
        url: `users/${userId}/purchase-items/${purchaseItemId}`,
        method: "PATCH",
        body: {
          bookingStatus,
          attorneyId,
          documentId,
        },
      }),
      invalidatesTags: ["purchaseItems"],
    }),
    getPurchaseItemsWithEvents: builder.query<
      PurchaseItemWithEventsGetResponse,
      { id: string; filter: EventFilter }
    >({
      query: ({ id, filter }) => ({
        url: `users/${id}/purchase-items/events?filter=${filter}`,
        method: "GET",
      }),
      providesTags: ["purchaseItems"],
    }),
    refundPurchaseItem: builder.mutation<
      PurchaseItemRefundResponse,
      {
        purchaseItemId: string;
        refundAmount: number | null;
        refundReason: string;
        isComplimentaryRefund?: boolean;
      }
    >({
      query: ({
        purchaseItemId,
        refundAmount,
        refundReason,
        isComplimentaryRefund,
      }) => ({
        url: `admin/purchase-items/${purchaseItemId}/refund`,
        method: "POST",
        body: {
          refundAmount,
          refundReason,
          isComplimentaryRefund: isComplimentaryRefund ?? false,
        },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(documentsApi.util.invalidateTags(["documents"]));
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["purchaseItems"],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetAttorneyServicesPurchasedByUserQuery,
  useGetPurchaseItemsByUserQuery,
  useGetCartsByUserQuery,
  useGetCartByIdQuery,
  useCreateCartMutation,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
  useAddCartItemMutation,
  useCreateCheckoutMutation,
  useApplyPromoCodeMutation,
  useRemovePromoCodeMutation,
  useUpdatePurchaseItemMutation,
  useGetPurchaseItemsWithEventsQuery,
  useRefundPurchaseItemMutation,
} = paymentsApi;
